import get from 'lodash/get';
import omit from 'lodash/omit';
import {useEffect, useState} from 'react';
import {connect} from 'react-redux';
import {useNavigate, useSearchParams} from 'react-router-dom';

import AlreadyScheduledDialog from '../../components/auth/AlreadyScheduledDialog';
import PatientSignupLayout from '../../components/patientSignup/PatientSignupLayout/PatientSignupLayout';
import NewPatientScreen from '../../components/patientSignup/NewPatientScreen';
import {setCredentials, setDeviceToken} from '../../redux/reducers/auth';
import {reset, setHasGuardian, setNumSteps} from '../../redux/reducers/patientSignup';
import {track} from '../../redux/reducers/analytics';
import {setProviderId} from '../../redux/reducers/patientSignup';
import {useCreatePatientMutation} from '../../redux/services/patientApi';
import {useCreateProviderMutation} from '../../redux/services/providerApi';
import {shuffle} from "../../helpers/signupRandomBackground";

const handleSubmit = ({
  props, 
  createUser, 
  setError, 
  alreadyScheduled, 
  selectedProviderId,
  navigate,
  isProvider,
}) => async (formData, options) => {
  props.track({eventName: 'signup.submittedSignupForm'});
  props.resetPatientSignup();
  const {recaptchaToken, hasGuardian} = options;
  props.setHasGuardian({hasGuardian});
  let body = {
    ...formData,
    has_guardian: hasGuardian,
    recaptchaToken
  };
  if (selectedProviderId) body.selected_provider_id = selectedProviderId;
  const resp = await createUser(body);
  const {data} = resp;
  if (resp.error) {
    props.track({eventName: 'signup.error', message: get(resp, 'error.data.errors[0]', 'Unknown error')});
    setError({
      message: get(
        resp,
        'error.data.errors[0]',
        'Unknown error'
      )
    });
  } else {
    setError(null);
    props.setCredentials({
      email: formData.email,
      user: omit(data, 'refresh', 'access'),
      refreshToken: data.refresh,
      accessToken: data.access
    });
    props.setDeviceToken({
      deviceToken: data.device_token,
      exp: data.expires_on,
      deviceEmail: formData.email,
    });
    if (selectedProviderId) props.setProviderId({providerId: selectedProviderId});
    if (!isProvider) {
      let forwardUrl = '/clients/onboarding';
      if (alreadyScheduled) forwardUrl += '?alreadyScheduled=true';
      if (selectedProviderId) forwardUrl += `&selectedProviderId=${selectedProviderId}`;
      navigate(forwardUrl);
    } else {
      navigate('/providers/');
    }
  }
};

const NewAccountPage = props => {
  const [error, setError] = useState(null);
  const [createPatient] = useCreatePatientMutation();
  const [createProvider] = useCreateProviderMutation();
  const isProvider = get(props, 'isProvider', false);
  const createUser = isProvider ? createProvider : createPatient;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const alreadyScheduled = searchParams.get('alreadyScheduled') === 'true';
  const selectedProviderId = searchParams.get('selectedProviderId') || null;
  const childProps = {
    ...props,
    subtitle: props.isProvider ? 'Create a provider account.' : props.subtitle,
    error,
    onSubmit: handleSubmit({
      props, 
      createUser, 
      setError, 
      alreadyScheduled,
      navigate,
      isProvider,
      selectedProviderId,
    }),
  };

  useEffect(() => {
    shuffle()
  }, []);

  return (
    <PatientSignupLayout {...childProps}>
      <AlreadyScheduledDialog alreadyScheduled={alreadyScheduled} providerId={selectedProviderId}/>
      <NewPatientScreen {...childProps} />
    </PatientSignupLayout>
  );
};

NewAccountPage.defaultProps = {
  title: 'Create an Account',
  subtitle: 'We are excited to provide you with the best possible care. Registering takes only a few minutes. First, let\'s create your MovementX account.',
  buttonText: null,
  step: 1,
  numSteps: 7,
  hideBackButton: true,
  backgroundColor: '#F9F9F9',
};

const mapState = state => ({
  numSteps: state.patientSignup.numSteps,
});

const mapDispatch = dispatch => ({
  setCredentials: params => dispatch(setCredentials(params)),
  setDeviceToken: params => dispatch(setDeviceToken(params)),
  setHasGuardian: params => dispatch(setHasGuardian(params)),
  resetPatientSignup: params => dispatch(reset(params)),
  setNumSteps: params => dispatch(setNumSteps(params)),
  setProviderId: params => dispatch(setProviderId(params)),
  track: params => dispatch(track(params)),
});

export default connect(mapState,mapDispatch)(NewAccountPage);
